import type {
  CategoryDecisions,
  EffectiveTrackingConsentPolicy,
  ResourceTrackingConsentScope,
  StoredTrackingConsentDecision,
  TrackingConsentScope
} from './types';

const COOKIE_PREFIX = 'gc_tc_';
const SECONDS_PER_DAY = 24 * 60 * 60;
// Consent cookies ride along on every request to the domain, so this cap trades request header
// bytes against re-prompting a visitor who decided on more events than this within one lifetime.
const MAX_CONSENT_COOKIES = 20;
// Browsers cap cookie expiry at 400 days, so a consent cookie older than that cannot have
// been written legitimately regardless of which organization's policy produced it.
const MAX_CONSENT_COOKIE_AGE_DAYS = 400;

type StoredConsent = {
  c: Record<string, StoredTrackingConsentDecision>;
  policy_version: string;
  decided_at: string;
};

type ParsedConsent = StoredConsent & {
  decidedAt: Date;
};

type CookieEntry = {
  name: string;
  value: string;
};

type CookieLocation = Pick<Location, 'hostname' | 'protocol'>;

const scopePrefixes: Record<TrackingConsentScope['type'], string> = {
  event: 'e',
  series: 's',
  hub: 'h',
  organization: 'o'
};

export const hasExactKeys = (candidate: object, expectedKeys: readonly string[]): boolean => {
  const actual = Object.keys(candidate).sort();
  const expected = [...expectedKeys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
};

const isConsentEnvelope = (value: unknown): value is StoredConsent => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<StoredConsent>;
  if (!candidate.c || typeof candidate.c !== 'object' || Array.isArray(candidate.c)) {
    return false;
  }
  if (
    !Object.values(candidate.c).every(decision => decision === 'allowed' || decision === 'rejected') ||
    typeof candidate.policy_version !== 'string' ||
    candidate.policy_version.length === 0 ||
    typeof candidate.decided_at !== 'string'
  ) {
    return false;
  }
  return true;
};

const parseConsentEnvelope = (value: string): ParsedConsent | null => {
  try {
    const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const parsed = JSON.parse(atob(padded)) as unknown;
    if (!isConsentEnvelope(parsed)) {
      return null;
    }
    const decidedAt = new Date(parsed.decided_at);
    return Number.isNaN(decidedAt.getTime()) ? null : { ...parsed, decidedAt };
  } catch {
    return null;
  }
};

const parseConsent = (value: string, expectedKeys: readonly string[]): ParsedConsent | null => {
  const parsed = parseConsentEnvelope(value);
  return parsed && hasExactKeys(parsed.c, expectedKeys) ? parsed : null;
};

export const encodeConsent = (
  decisions: Record<string, StoredTrackingConsentDecision>,
  policyVersion: string,
  decidedAt: Date
): string =>
  btoa(
    JSON.stringify({
      c: decisions,
      policy_version: policyVersion,
      decided_at: decidedAt.toISOString()
    })
  )
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/u, '');

export const decodeConsent = (
  value: string,
  expectedKeys: readonly string[],
  policyVersion: string,
  preferenceLifetimeDays: number,
  now: () => Date
): Readonly<Record<string, StoredTrackingConsentDecision>> | null => {
  // A non-finite window makes every age comparison below false, so an unchecked
  // caller would treat the stored decision as valid forever.
  if (!Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return null;
  }
  const parsed = parseConsent(value, expectedKeys);
  if (
    !parsed ||
    parsed.policy_version !== policyVersion ||
    isExpired(parsed.decidedAt, now(), preferenceLifetimeDays)
  ) {
    return null;
  }
  return Object.freeze({ ...parsed.c });
};

const parseCookies = (cookieHeader: string): CookieEntry[] => {
  const cookies: CookieEntry[] = [];

  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0) {
      continue;
    }

    const name = part.slice(0, separator).trim();
    if (!name) {
      continue;
    }

    cookies.push({ name, value: part.slice(separator + 1).trim() });
  }

  return cookies;
};

const cookieValues = (cookies: CookieEntry[], name: string): string[] =>
  cookies.filter(cookie => cookie.name === name).map(cookie => cookie.value);

const isExpired = (decidedAt: Date, now: Date, preferenceLifetimeDays: number): boolean => {
  const age = now.getTime() - decidedAt.getTime();
  return age < 0 || age >= preferenceLifetimeDays * SECONDS_PER_DAY * 1000;
};

const cookieAttributes = ({
  location,
  maxAge,
  cookieDomain
}: {
  location: CookieLocation;
  maxAge: number;
  cookieDomain?: string;
}): string =>
  [
    `Max-Age=${maxAge}`,
    'Path=/',
    'SameSite=Lax',
    location.protocol === 'https:' ? 'Secure' : '',
    cookieDomain ? `Domain=${cookieDomain}` : ''
  ]
    .filter(Boolean)
    .join('; ');

const deleteCookie = (document: Document, location: CookieLocation, name: string, cookieDomain?: string): void => {
  const domain = cookieDomain ? `; Domain=${cookieDomain}` : '';
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}${domain}`;
};

const deleteConsentCookie = (
  document: Document,
  location: CookieLocation,
  name: string,
  cookieDomain?: string
): void => {
  deleteCookie(document, location, name);
  if (cookieDomain) {
    deleteCookie(document, location, name, cookieDomain);
  }
};

const canSetCookieDomain = (hostname: string, cookieDomain: string): boolean => {
  const host = hostname.toLowerCase().replace(/\.$/u, '');
  const domain = cookieDomain.toLowerCase().replace(/\.$/u, '');
  return host === domain || host.endsWith(`.${domain}`);
};

export const buildTrackingConsentCookieName = (scope: TrackingConsentScope): string => {
  const resourceId = scope.id.replaceAll('-', '');
  if (!resourceId || !/^[a-zA-Z0-9_]+$/u.test(resourceId)) {
    throw new Error('Tracking consent scope ID must be alphanumeric');
  }

  return `${COOKIE_PREFIX}${scopePrefixes[scope.type]}_${resourceId}`;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

const MAX_COOKIE_DOMAIN_LENGTH = 253;
const MAX_DOMAIN_LABEL_LENGTH = 63;
// One class, one quantifier: whatever the input, this cannot backtrack. Expressing the
// whole domain as a single pattern instead nests quantifiers, and nothing at the call
// site tells a reader — or a scanner — that such a pattern is safe on hostile input.
const DOMAIN_LABEL_CHARS_PATTERN = /^[a-z0-9-]+$/iu;

const isDomainLabel = (label: string): boolean =>
  label.length <= MAX_DOMAIN_LABEL_LENGTH &&
  !label.startsWith('-') &&
  !label.endsWith('-') &&
  DOMAIN_LABEL_CHARS_PATTERN.test(label);

const isValidCookieDomain = (value: string): boolean => {
  if (value.length === 0 || value.length > MAX_COOKIE_DOMAIN_LENGTH) {
    return false;
  }
  const labels = value.split('.');
  return labels.length > 1 && labels.every(isDomainLabel);
};

export const selectTrackingConsentCookieScope = (
  policy: Pick<EffectiveTrackingConsentPolicy, 'consent_scope'>,
  resourceScope: ResourceTrackingConsentScope
): TrackingConsentScope => {
  const delivered = policy.consent_scope;
  if (
    delivered?.type !== 'domain' ||
    typeof delivered.organization_id !== 'string' ||
    !UUID_PATTERN.test(delivered.organization_id) ||
    typeof delivered.cookie_domain !== 'string' ||
    !isValidCookieDomain(delivered.cookie_domain)
  ) {
    return resourceScope;
  }
  return {
    type: 'organization',
    id: delivered.organization_id,
    cookieDomain: delivered.cookie_domain.toLowerCase()
  };
};

export const readTrackingConsentDecisions = ({
  document,
  scope,
  expectedKeys,
  policyVersion,
  preferenceLifetimeDays,
  now = () => new Date()
}: {
  document: Document;
  scope: TrackingConsentScope;
  expectedKeys: readonly string[];
  policyVersion: string;
  preferenceLifetimeDays: number;
  now?: () => Date;
}): CategoryDecisions => {
  const unknown = Object.freeze(Object.fromEntries(expectedKeys.map(key => [key, 'unknown']))) as CategoryDecisions;
  if (!Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return unknown;
  }
  try {
    const values = cookieValues(parseCookies(document.cookie), buildTrackingConsentCookieName(scope));
    if (values.length !== 1) {
      return unknown;
    }
    return decodeConsent(values[0], expectedKeys, policyVersion, preferenceLifetimeDays, now) ?? unknown;
  } catch {
    return unknown;
  }
};

export const writeTrackingConsentDecisions = ({
  document,
  location,
  scope,
  expectedKeys,
  decisions,
  policyVersion,
  preferenceLifetimeDays,
  now = () => new Date()
}: {
  document: Document;
  location: CookieLocation;
  scope: TrackingConsentScope;
  expectedKeys: readonly string[];
  decisions: Record<string, StoredTrackingConsentDecision>;
  policyVersion: string;
  preferenceLifetimeDays: number;
  now?: () => Date;
}): boolean => {
  if (
    !Number.isFinite(preferenceLifetimeDays) ||
    preferenceLifetimeDays <= 0 ||
    !hasExactKeys(decisions, expectedKeys) ||
    (scope.type === 'organization' && !canSetCookieDomain(location.hostname, scope.cookieDomain))
  ) {
    return false;
  }

  try {
    const decisionTime = now();
    const name = buildTrackingConsentCookieName(scope);
    const value = encodeConsent(decisions, policyVersion, decisionTime);
    const cookieDomain = scope.type === 'organization' ? scope.cookieDomain : undefined;

    if (cookieDomain) {
      // Remove a stale host-only organization cookie before writing the parent-domain
      // cookie with the same name. Duplicate names fail closed during reads.
      deleteCookie(document, location, name);
    }

    document.cookie = `${name}=${value}; ${cookieAttributes({
      location,
      maxAge: Math.floor(preferenceLifetimeDays * SECONDS_PER_DAY),
      cookieDomain
    })}`;

    if (cookieValues(parseCookies(document.cookie), name)[0] !== value) {
      return false;
    }

    // Cookies under this prefix can belong to other resources and other organizations whose
    // category keys, policy version and retention window are all unknown here, so this sweep
    // only enforces invariants that hold for every Goldcast consent cookie. Each resource
    // re-validates its own cookie against its own policy on read.
    const consentCookies = parseCookies(document.cookie)
      .filter(cookie => cookie.name.startsWith(COOKIE_PREFIX))
      .map(cookie => ({
        name: cookie.name,
        consent: parseConsentEnvelope(cookie.value)
      }));

    const retainedCookies: Array<{ name: string; decidedAt: Date }> = [];
    for (const cookie of consentCookies) {
      if (
        !cookie.consent ||
        cookie.consent.decidedAt.getTime() > decisionTime.getTime() ||
        isExpired(cookie.consent.decidedAt, decisionTime, MAX_CONSENT_COOKIE_AGE_DAYS)
      ) {
        deleteConsentCookie(document, location, cookie.name, cookieDomain);
        continue;
      }
      retainedCookies.push({
        name: cookie.name,
        decidedAt: cookie.consent.decidedAt
      });
    }

    retainedCookies.sort((left, right) => {
      const byTime = right.decidedAt.getTime() - left.decidedAt.getTime();
      if (byTime !== 0) {
        return byTime;
      }
      if (left.name === name) {
        return -1;
      }
      if (right.name === name) {
        return 1;
      }
      return left.name.localeCompare(right.name);
    });

    for (const cookieName of new Set(retainedCookies.slice(MAX_CONSENT_COOKIES).map(cookie => cookie.name))) {
      deleteConsentCookie(document, location, cookieName, cookieDomain);
    }

    const finalValues = cookieValues(parseCookies(document.cookie), name);
    return finalValues.length === 1 && finalValues[0] === value;
  } catch {
    return false;
  }
};

export const readTrackingConsentDecisionsFromHeader = (
  cookieHeader: string | undefined,
  scope: TrackingConsentScope,
  expectedKeys: readonly string[],
  policyVersion: string,
  preferenceLifetimeDays: number,
  now: () => Date = () => new Date()
): CategoryDecisions => {
  const unknown = Object.freeze(Object.fromEntries(expectedKeys.map(key => [key, 'unknown']))) as CategoryDecisions;
  if (!cookieHeader || !Number.isFinite(preferenceLifetimeDays) || preferenceLifetimeDays <= 0) {
    return unknown;
  }
  try {
    const values = cookieValues(parseCookies(cookieHeader), buildTrackingConsentCookieName(scope));
    if (values.length !== 1) {
      return unknown;
    }
    return decodeConsent(values[0], expectedKeys, policyVersion, preferenceLifetimeDays, now) ?? unknown;
  } catch {
    return unknown;
  }
};
